<template>
  <div v-if="isOpen" class="bg-natural-white-60 absolute inset-0">
    <div class="Drawer__header m-2.5 flex h-22 items-center">
      <h3 class="m-auto text-xl" v-text="giftNoteTitle" />
      <button
        aria-label="Close Gift Note"
        class="mb-auto hover:cursor-pointer"
        @click="handleCloseGiftNote"
      >
        <Icon name="close" class-name="size-8" />
      </button>
    </div>
    <div class="flex h-[calc(100%-108px)] flex-col px-2 sm:px-15">
      <div class="flex flex-col gap-8">
        <div class="flex flex-col gap-2">
          <label for="gift-note" class="text-normal font-medium uppercase">
            Gift Message (Optional)
          </label>
          <textarea
            id="gift-note"
            v-model="giftNote"
            rows="5"
            maxlength="160"
            placeholder="Type your message here, but avoid emojis and special characters."
            class="w-full rounded-xl bg-white px-5 py-2"
          ></textarea>
          <div
            class="font-light-charcoal flex gap-1 text-sm"
            :class="{ 'text-red-500': MAX_CHARACTERS - giftNote.length === 0 }"
          >
            <span v-text="MAX_CHARACTERS - giftNote.length" />
            Characters Remaining
          </div>
        </div>
        <div class="rounded-xl bg-white p-6">
          <p class="text-normal mb-3 font-medium uppercase">
            Logo-Free Packaging
            <span class="text-light-charcoal text-xs"> (This will be hidden on the box) </span>
          </p>
          <div class="flex items-center gap-3">
            <input
              id="logos-free"
              v-model="isLogoFree"
              type="checkbox"
              aria-label="Hide the logos on the box. "
              :checked="isLogoFree"
              class="checkbox my-auto"
            />
            <label for="logos-free" class="text-light-charcoal max-w-80 text-sm">
              Let’s keep it a surprise. Hide the logos on the box.
            </label>
          </div>
        </div>
      </div>
      <div class="mt-auto mb-3 flex justify-end gap-2">
        <button
          class="btn btn btn-secondary w-full hover:border-black"
          :disabled="!isGiftNoteValid()"
          @click="handleSaveGiftNote"
        >
          Save
        </button>
        <button class="btn btn-secondary w-full hover:border-black" @click="handleCancelGiftNote">
          Cancel
        </button>
      </div>
    </div>
  </div>
  <div
    v-else
    class="bg-natural-white sans-serif flex w-full items-center justify-center py-2 text-xs font-medium"
  >
    <button
      aria-label="Open Gift Note"
      class="flex gap-1 px-2 py-1 text-xs font-medium hover:cursor-pointer"
      @click="handleOpenGiftNote"
    >
      <span v-text="props.giftNoteLabel" />
      <span>+</span>
    </button>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';

import Icon from '~/components/icon-svg.vue';
import { useCart } from '~/composables/use-cart';
import { useCartDrawerVisibility } from '~/composables/use-cart-drawer-visibility';
import * as DataDogLogs from '~/lib/datadog/datadog';

const props = defineProps({
  giftNoteLabel: {
    default: '',
    type: String,
  },
  giftNoteTitle: {
    default: '',
    type: String,
  },
});

const MAX_CHARACTERS = 160;

const giftNote = ref('');
const isOpen = ref(false);
const isLogoFree = ref(false);
const visibility = useCartDrawerVisibility();

const { addAttributes, cart } = useCart();

const handleOpenGiftNote = () => {
  isOpen.value = true;
};

const isGiftNoteValid = () => giftNote.value.length <= MAX_CHARACTERS;

watch(visibility.isOpen, (isCartDrawerOpen) => {
  if (!isCartDrawerOpen) {
    isOpen.value = false;
  }
});

const handleCloseGiftNote = () => {
  isOpen.value = false;
};

const handleSaveGiftNote = async () => {
  try {
    await addAttributes({
      gift_message: giftNote.value,
      logo_free: isLogoFree.value,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds component] - Cart Gift Note - handleSaveGiftNote - Error saving gift note',
      { error },
    );
  }
  isOpen.value = false;
};

const handleCancelGiftNote = () => {
  isOpen.value = false;
};

onMounted(async () => {
  if (cart?.value?.attributes) {
    const { gift_message, logo_free } = cart.value.attributes;
    giftNote.value = gift_message || '';
    isLogoFree.value = logo_free || false;
  }
});
</script>
